/*!
 * Copyright © 2018-2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Environment } from './environment.interface';

export const environment: Environment = {
  production: true,
  buildTimestamp: '1792676255685',
  config: {
    revealFeature: '8',
    googleMapsApiKey: '#{GOOGLE_MAPS_API_KEY}',
    googleMapsApiVersion: '#{GOOGLE_MAPS_API_VERSION}',
    endpoints: {
      customerMetadataApiEndpoint: '#{BASE_CUSTOMER_METADATA}',
      plotApiEndpoint: '#{BASE_PLOT}',
      reportsApiEndpoint: '#{BASE_REPORTS}',
      uiSharedServicesApiEndpoint: '#{BASE_SHARED_SERVICES}',
      placesApiEndpoint: '#{BASE_PLACES}',
      balloonApiEndpoint: '#{BASE_BALLOON}',
      realTimeDataApiEndpoint: '#{BASE_REAL_TIME_DATA}',
      notificationsApiEndpoint: '#{BASE_NOTIFICATION}',
      planningSchedulingApiEndpoint: '#{BASE_PLANNING_SCHEDULING}',
      addressResolutionApiEndpoint: '#{BASE_ADDRESS_RESOLUTION}',
      deviceStatusApiEndpoint: '#{BASE_DEVICE_STATUS}',
      categoriesApiEndpoint: '#{BASE_CATEGORIES}',
      alertApiEndpoint: '#{BASE_ALERT}',
      videoEventApiEndpoint: '#{BASE_VIDEO_EVENT}',
      videoCameraApiEndpoint: '#{BASE_CAMERA_EVENT}',
      workingTimeDirectiveApiEndpoint: '#{BASE_WORKING_TIME_DIRECTIVE}',
      assetsApiEndpoint: '#{BASE_ASSETS_API}',
      roadsideAssistanceApiEndpoint: '#{BASE_ROADSIDE_ASSISTANCE}',
      schedulerApiEndpoint: '#{BASE_SCHEDULER}',
      searchlightApiEndpoint: '#{BASE_SEARCH}',
      vehicleApiEndpoint: '#{BASE_VEHICLE}',
      deviceInstallApiEndpoint: '#{BASE_DEVICE_INSTALL}',
      fleetApiEndpoint: '#{BASE_FLEET_API}',
      customerSpeedApiEndpoint: '#{BASE_CUSTOMER_SPEED_API}',
      attributeApiEndpoint: '#{BASE_ATTRIBUTE_API}'
    },
    websocketUrl: '#{BASE_WEBSOCKET}',
    legacyBaseUrl: '#{BASE_LEGACY}',
    videoExperienceApiBaseUrl: '#{VIDEO_EXPERIENCE_API_BASE_URL}',
    auth: {
      shouldKeepSessionAlive: true,
      keepSessionAliveEndpoint: '#{AUTH_KEEP_SESSION_ALIVE_ENDPOINT}',
      loginPageUrl: '#{LOGIN_PAGE_URL}',
      legacyLogoutEndpoint: '#{LEGACY_LOGOUT_ENDPOINT}',
      whiteListedUrlsRegex: ['#{AUTH_WHITE_LISTED_URLS_REGEX}'],
      refreshEndpoint: '#{AUTH_REFRESH_ENDPOINT}',
      clientId: '#{AUTH_CLIENT_ID}',
      logoutEndpoint: '#{AUTH_LOGOUT_ENDPOINT}',
      logoutRedirectEndpoint: '#{AUTH_LOGOUT_REDIRECT_ENDPOINT}',
      refreshIfExpired: /true|1/i.test('#{AUTH_REFRESH_IF_EXPIRED}')
    },
    navbar: {
      appBaseUrl: '#{APP_BASE_URL}',
      assetsDomain: '#{BASE_ASSETS}',
      assetsBaseHref: '',
      endpoints: {
        notificationsApiEndpoint: '#{BASE_NOTIFICATION}',
        uiSharedServicesApiEndpoint: '#{BASE_SHARED_SERVICES}',
        customerMetadataApiEndpoint: '#{BASE_CUSTOMER_METADATA}'
      },
      liveMapPendoGuideId: 'ZuNYJC3UOEzazgcaBJhHs-_fNuY',
      helpPanelEnabled: /true|1/i.test('#{HELPPANEL_ENABLED}')
    },
    disableSsrApis: false,
    isWorkingTimeDirectiveEnabled: true,
    videoEventPath: '#{VIDEO_EVENT_PATH}',
    roadsideAssistanceMicrositeUrl: '#{ROADSIDE_ASSISTANCE_MICROSITE_URL}',
    driverPlannerPagePath: '#{DRIVER_PLANNER_PAGE_PATH}',
    isPushServiceDisabled: false,
    workingTimeDirectiveInterval: '#{WORKING_TIME_DIRECTIVE_INTERVAL}',
    isSchedulerEnabled: /true|1/i.test('#{TOGGLE_SCHEDULER}'),
    pendo: {
      enabled: /true|1/i.test('#{PENDO_ENABLED}'),
      prefix: '#{PENDO_PREFIX}'
    },
    helpPanel: {
      featureToggleUrl: '#{FEATURETOGGLE_URL}',
      inContactExternalChatUrl: '#{INCONTACT_EXTERNAL_CHAT_URL}',
      whatsNewEnabled: /true|1/i.test('#{WHATS_NEW_ENABLED}')
    },
    notificationPipeLineSettings: {
      isPipelineEnabledForMicrosite: /true|1/i.test('#{NOTIFICATIONS_PIPELINE_ENABLED}'),
      notificationsApiPath: '#{NOTIFICATIONS_PIPELINE_API}'
    },
    isAssetsOnPanelEnabled: /true|1/i.test('#{ASSETS_ON_PANEL_ENABLED}'),
    replayUpdateThrottleMs: '#{REPLAY_UPDATE_THROTTLE_MS}',
    notificationsTemporaryTimeoutMs: '#{NOTIFICATION_TEMPORARY_TIMEOUT_MS}',
    clusteringByDefaultMaxPlots: '#{CLUSTERING_BY_DEFAULT_MAX_PLOTS}',
    selectionLimitOverride: '#{SELECTION_LIMIT_OVERRIDE}',
    deviceHealthCheckLiveChat: {
      enabled: /true|1/i.test('#{DHC_CHAT_ENABLED}'),
      busNo: '#{DHC_CHAT_BUS_NO}',
      poc: '#{DHC_CHAT_POC}',
      scriptUrl: '#{DHC_CHAT_SCRIPT_URL}',
      serverHost: '#{DHC_CHAT_SERVER_HOST}'
    },
    deviceHealthCheckAmazonConnectChat: {
      enabled: /true|1/i.test('#{DHC_AC_CHAT_ENABLED}'),
      scriptUrl: '#{DHC_AC_CHAT_SCRIPT_URL}',
      instanceId: '#{DHC_AC_CHAT_INSTANCE_ID}',
      snippetId: '#{DHC_AC_CHAT_SNIPPET_ID}'
    },
    deviceHealthCheckAgentCoreChat: {
      enabled: /true|1/i.test('#{DHC_AGENT_CORE_CHAT_ENABLED}'),
      endpoint: '#{DHC_AGENT_CORE_URL}',
      scriptUrl: '#{DHC_AGENT_CORE_CHAT_SCRIPT_URL}',
      loaderUrl: '#{DHC_AGENT_CORE_CHAT_LOADER_URL}',
      authHost: '#{DHC_AGENT_CORE_AUTH_HOST}',
      authClientId: '#{DHC_AGENT_CORE_AUTH_CLIENT_ID}'
    },
    enableLiveMapECMBalloon: /true|1/i.test('#{ENABLE_LIVE_MAP_ECM_BALLOON}'),
    ecmPanelVisibleFields: '#{ECM_PANEL_VISIBLE_FIELDS}'
      .split(',')
      .map(f => f.trim())
      .filter(Boolean),
    realTimeSocketValuesToIgnore: '#{REAL_TIME_SOCKET_VALUES_TO_IGNORE}'
      .split(',')
      .map(f => f.trim())
      .filter(Boolean),
    ecmViewVehicleUrl: '#{ECM_VIEW_VEHICLE_URL}',
    ecmDtcHistoryUrl: '#{ECM_DTC_HISTORY_URL}',
    myAccountHost: {
      viewVehicleUrl: '#{MYACCOUNT_ECM_VIEW_VEHICLE_URL}',
      dtcHistoryUrl: '#{MYACCOUNT_ECM_DTC_HISTORY_URL}',
      viewAssetUrl: '#{MYACCOUNT_FLEET_ASSET_URL}',
      driverListUrl: '#{MYACCOUNT_DRIVER_LIST_URL}',
      assetLocationHistoryUrl: '#{MYACCOUNT_ASSET_LOCATION_HISTORY_URL}',
      vehiclesLiveUrl: '#{MYACCOUNT_VEHICLES_LIVE_URL}',
      trackerTroubleshootBaseUrl: '#{MYACCOUNT_TRACKER_TROUBLESHOOT_BASE_URL}',
      cameraListBaseUrl: '#{MYACCOUNT_CAMERA_LIST_BASE_URL}',
      videoListBaseUrl: '#{MYACCOUNT_VIDEO_LIST_BASE_URL}'
    },
    enableManageSpeedLimit: /true|1/i.test('#{ENABLE_LIVE_MAP_SPEED_LIMIT}'),
    logoutLoggingAccounts: '#{LOGOUT_LOGGING_ACCOUNTS}',
    managePostedSpeedHelpUrl: '#{MANAGE_POSTED_SPEED_HELP_URL}',
    insightsPanel: {
      pageNameKey: '#{LIVE_MAP_PAGE_NAME_KEY}',
      insightsMicrosite: '#{INSIGHTS_MICROSITE}',
      insightsApi: '#{INSIGHTS_API}',
      enabled: /true|1/i.test('#{INSIGHTS_PANEL_ENABLED}'),
      isBeta: /true|1/i.test('#{INSIGHTS_PANEL_IS_BETA}'),
      version: '#{INSIGHTS_PANEL_VERSION}'
    },
    notificationsClientSettings: {
      websocketsUrl: '#{BASE_WEBSOCKET}',
      clientProtocolVersion: ''
    }
  },
  overrideBaseUrl: undefined
};
